import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import { colors } from "components/Theme/WebTheme";

// Endpoints públicos servidos na MESMA origem via rewrites do Firebase Hosting.
// Validam o OTP no servidor; a página nunca toca o Firebase diretamente.
const callFn = async (path, body) => {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return res.json();
};

const useStyles = makeStyles(() => ({
  page: {
    minHeight: "100vh",
    background: "#f4f5f7",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "24px 12px",
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 460,
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  header: {
    background: colors.Header || "#0aa89e",
    color: "#fff",
    padding: "20px 24px",
  },
  headerTitle: { margin: 0, fontSize: 20, fontWeight: 700 },
  headerSub: { margin: "6px 0 0", fontSize: 13, opacity: 0.9 },
  body: { padding: 24 },
  label: { fontSize: 12, color: "#8a8f99", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 22,
    letterSpacing: 6,
    textAlign: "center",
    border: "1px solid #d9dce1",
    borderRadius: 10,
    boxSizing: "border-box",
    outline: "none",
  },
  btn: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
    background: colors.Action_Button_Back || "#0aa89e",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    marginTop: 16,
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  section: {
    borderTop: "1px solid #eef0f2",
    paddingTop: 16,
    marginTop: 16,
  },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 8 },
  row: { display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 14 },
  rowKey: { color: "#8a8f99" },
  rowVal: { color: "#222", fontWeight: 600, textAlign: "right", maxWidth: "60%" },
  amount: { fontSize: 28, fontWeight: 800, color: colors.Header || "#0aa89e", textAlign: "center", margin: "8px 0" },
  pmRow: { display: "flex", gap: 8, marginTop: 8 },
  pm: {
    flex: 1,
    padding: "12px 6px",
    textAlign: "center",
    border: "1px solid #d9dce1",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    color: "#444",
    background: "#fff",
  },
  pmActive: { borderColor: colors.Header || "#0aa89e", color: colors.Header || "#0aa89e", background: "rgba(10,168,158,0.08)" },
  select: {
    width: "100%",
    padding: "12px 14px",
    fontSize: 15,
    border: "1px solid #d9dce1",
    borderRadius: 10,
    boxSizing: "border-box",
    background: "#fff",
    marginBottom: 4,
  },
  btnOnline: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
    background: colors.Header || "#0aa89e",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    marginTop: 12,
  },
  orDivider: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    color: "#aab",
    fontSize: 12,
    margin: "20px 0 4px",
  },
  hint: { fontSize: 12, color: "#8a8f99", marginTop: 6 },
  error: { color: "#c0392b", fontSize: 14, marginTop: 12, textAlign: "center" },
  done: { textAlign: "center", padding: "12px 0" },
  doneIcon: { fontSize: 56 },
  doneTitle: { fontSize: 20, fontWeight: 800, color: colors.Header || "#0aa89e", margin: "8px 0" },
  stepTag: { fontSize: 12, fontWeight: 700, color: colors.Header || "#0aa89e", marginBottom: 4 },
}));

export default function DeliveryPayment() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { id } = useParams();

  // Tradução com fallback: react-i18next devolve a própria chave quando não há
  // tradução; nesse caso usamos o texto padrão para não exibir nomes crus.
  const tt = (key, fallback) => {
    const v = t(key);
    return (!v || v === key) ? fallback : v;
  };

  const [step, setStep] = useState("otp"); // otp | seen | received | done
  const [otp, setOtp] = useState("");
  const [data, setData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [providers, setProviders] = useState([]); // gateways online ativos
  const [gateway, setGateway] = useState("");

  // Lista os gateways online ativos (mesma fonte do checkout normal do app).
  useEffect(() => {
    fetch("/get_providers")
      .then((r) => r.json())
      .then((list) => {
        if (Array.isArray(list) && list.length > 0) {
          setProviders(list);
          setGateway(list[0].name);
        }
      })
      .catch(() => {});
  }, []);

  // Ao enviar o form de pagamento online para /{gateway}-link, os providers derivam
  // a URL de retorno (back_url) do header Referer. A partir de /entrega/:id (caminho
  // de 2 segmentos) isso geraria uma back_url quebrada; suprimindo o Referer, todos
  // caem no fallback baseado no host -> https://<dominio>/ (mesmo caminho do mobile).
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      if (meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  const errText = (code) => {
    const map = {
      session_not_found: t("delivery_error_session_not_found"),
      booking_not_found: t("delivery_error_session_not_found"),
      otp_mismatch: t("delivery_error_otp_mismatch"),
      session_expired: t("delivery_error_session_expired"),
      too_many_attempts: t("delivery_error_too_many_attempts"),
    };
    return map[code] || t("delivery_error_generic");
  };

  const stepFromStatus = (status) => {
    if (status === "COMPLETE" || status === "PAID") return "done";
    if (["ARRIVED", "STARTED", "REACHED"].includes(status)) return "received";
    return "seen";
  };

  const fmtAmount = () => {
    if (!data) return "";
    const a = data.amounts || {};
    const total = (parseFloat(a.trip_cost || 0) - parseFloat(a.discount || 0)).toFixed(a.decimal || 2);
    return `${a.symbol || ""} ${total}`.trim();
  };

  // Valor numérico puro (sem símbolo) enviado ao gateway de pagamento.
  const totalNumber = () => {
    const a = (data && data.amounts) || {};
    return (parseFloat(a.trip_cost || 0) - parseFloat(a.discount || 0)).toFixed(a.decimal || 2);
  };

  // Dupla validação do pagamento externo (vem do delivery_session_verify, já
  // combinando os flags global + externo). `undefined` = permitido (compat. com
  // backend ainda não redeployado); só esconde quando explicitamente false.
  const showOnline = (!data || data.externalOnlineEnabled !== false) && providers.length > 0;
  const showCash = !data || data.externalCashEnabled !== false;

  const verify = async () => {
    if (!otp || otp.length < 4) return;
    setLoading(true);
    setError("");
    try {
      const res = await callFn("/delivery_session_verify", { bookingId: id, otp });
      if (res && res.success) {
        setData(res);
        if (res.recipientPaymentMethod) setPaymentMethod(res.recipientPaymentMethod);
        setStep(stepFromStatus(res.status));
      } else {
        setError(errText(res && res.error));
      }
    } catch (e) {
      setError(t("delivery_error_generic"));
    }
    setLoading(false);
  };

  const confirmSeen = async () => {
    if (!paymentMethod) {
      setError(t("delivery_select_payment_method"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await callFn("/delivery_confirm_seen", { bookingId: id, otp, paymentMethod });
      if (res && res.success) {
        setStep("received");
      } else {
        setError(errText(res && res.error));
      }
    } catch (e) {
      setError(t("delivery_error_generic"));
    }
    setLoading(false);
  };

  const confirmReceived = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await callFn("/delivery_confirm_received", { bookingId: id, otp });
      if (res && res.success) {
        setStep("done");
      } else {
        setError(errText(res && res.error));
      }
    } catch (e) {
      setError(t("delivery_error_generic"));
    }
    setLoading(false);
  };

  const Row = ({ k, v }) =>
    v ? (
      <div className={classes.row}>
        <span className={classes.rowKey}>{k}</span>
        <span className={classes.rowVal}>{v}</span>
      </div>
    ) : null;

  const Details = () => {
    if (!data) return null;
    const d = data.driver || {};
    const ve = data.vehicle || {};
    const m = data.merchandise || {};
    const vehicle = [ve.make, ve.model].filter(Boolean).join(" ");
    return (
      <>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t("delivery_driver")}</div>
          <Row k={t("delivery_recipient")} v={data.recipientName} />
          <Row k={t("delivery_driver")} v={d.name} />
          <Row k={t("driver_contact") || "Contato"} v={d.contact} />
        </div>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t("delivery_vehicle")}</div>
          <Row k={t("delivery_vehicle")} v={vehicle} />
          <Row k={t("delivery_plate")} v={ve.plate} />
          <Row k={tt("vehicle_color", "Cor")} v={ve.color} />
        </div>
        {(data.merchandiseType || (m && Array.isArray(m.volumes) && m.volumes.length > 0) || m.totalVolumes) ? (
          <div className={classes.section}>
            <div className={classes.sectionTitle}>{tt("delivery_merchandise", "Encomenda")}</div>
            <Row k={tt("goods_type", "Tipo")} v={data.merchandiseType} />
            <Row k={tt("total_volumes", "Total de volumes")} v={m.totalVolumes} />
            <Row k={tt("total_weight", "Peso total")} v={m.totalPeso} />
            <Row k={tt("total_value", "Valor total")} v={m.totalValor} />
            {Array.isArray(m.volumes) ? m.volumes.map((v, i) => {
              const dims = [v.altura, v.largura, v.profundidade].filter(Boolean).join(" x ");
              const parts = [
                v.produto,
                v.quantidade ? `${tt("qty", "Qtd")}: ${v.quantidade}` : null,
                v.peso ? `${tt("weight", "Peso")}: ${v.peso}` : null,
                v.valor ? `${tt("value", "Valor")}: ${v.valor}` : null,
                dims || null,
                v.notaFiscal ? `${tt("invoice", "NF")}: ${v.notaFiscal}` : null,
              ].filter(Boolean).join(" · ");
              return (
                <div key={i} className={classes.row}>
                  <span className={classes.rowVal} style={{ maxWidth: "100%", textAlign: "left", fontWeight: 500 }}>• {parts}</span>
                </div>
              );
            }) : null}
          </div>
        ) : null}
        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t("delivery_route")}</div>
          <Row k={t("delivery_pickup")} v={data.pickup} />
          <Row k={t("delivery_drop")} v={data.drop} />
        </div>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>{t("delivery_amount_to_pay")}</div>
          <div className={classes.amount}>{fmtAmount()}</div>
        </div>
      </>
    );
  };

  return (
    <div className={classes.page}>
      <div className={classes.card}>
        <div className={classes.header}>
          <h1 className={classes.headerTitle}>{t("delivery_page_title")}</h1>
          {data && data.reference ? (
            <p className={classes.headerSub}>{t("delivery_reference")}: {data.reference}</p>
          ) : null}
        </div>
        <div className={classes.body}>
          {step === "otp" && (
            <>
              <div className={classes.label}>{t("delivery_otp_prompt")}</div>
              <input
                className={classes.input}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="••••••"
              />
              <button
                className={`${classes.btn} ${loading || otp.length < 4 ? classes.btnDisabled : ""}`}
                onClick={verify}
                disabled={loading || otp.length < 4}
              >
                {loading ? "..." : t("delivery_access_btn")}
              </button>
            </>
          )}

          {step === "seen" && (
            <>
              <div className={classes.stepTag}>{t("delivery_step1_title")}</div>
              <Details />

              {/* Pagar online agora — gateways ativos (mesma fonte do app) */}
              {showOnline && (
                <div className={classes.section}>
                  <div className={classes.sectionTitle}>{tt("delivery_pay_online", "Pagar online agora")}</div>
                  {providers.length > 1 && (
                    <select
                      className={classes.select}
                      value={gateway}
                      onChange={(e) => setGateway(e.target.value)}
                    >
                      {providers.map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  )}
                  <form method="POST" action={`/${gateway}-link`}>
                    <input type="hidden" name="order_id" value={id} />
                    <input type="hidden" name="amount" value={totalNumber()} />
                    <input type="hidden" name="currency" value={(data && data.amounts && data.amounts.code) || "BRL"} />
                    <input type="hidden" name="product_name" value={tt("delivery_page_title", "Confirmação de Entrega")} />
                    <input type="hidden" name="first_name" value={(((data && data.recipientName) || "Cliente").trim().split(" ")[0]) || "Cliente"} />
                    <input type="hidden" name="last_name" value={((((data && data.recipientName) || "").trim().split(" ").slice(1).join(" ")) || ".")} />
                    <input type="hidden" name="email" value={(data && data.recipientEmail) || ""} />
                    <input type="hidden" name="quantity" value={1} />
                    <input type="hidden" name="cust_id" value={id} />
                    <input type="hidden" name="mobile_no" value={(data && data.recipientPhone) || ""} />
                    <input type="hidden" name="platform" value="web" />
                    <button type="submit" className={classes.btnOnline} disabled={!gateway}>
                      {tt("delivery_pay_online_btn", "Pagar online")} {fmtAmount()}
                    </button>
                  </form>
                  <div className={classes.hint}>{tt("delivery_pay_online_hint", "Você será direcionado ao ambiente seguro de pagamento. Após a confirmação, a entrega é registrada automaticamente.")}</div>
                </div>
              )}

              {/* divisor "ou" — só quando AMBAS as formas estão disponíveis */}
              {showOnline && showCash && (
                <div className={classes.orDivider}>
                  <span style={{ flex: 1, height: 1, background: "#eef0f2" }} />
                  <span style={{ padding: "0 10px" }}>{tt("delivery_or", "ou")}</span>
                  <span style={{ flex: 1, height: 1, background: "#eef0f2" }} />
                </div>
              )}

              {/* pagar presencialmente ao motorista (externo em dinheiro) */}
              {showCash && (
                <div className={classes.section} style={{ borderTop: "none", marginTop: 4, paddingTop: 4 }}>
                  <div className={classes.sectionTitle}>{tt("delivery_pay_to_driver", "Ou pagar direto ao motorista")}</div>
                  <div className={classes.pmRow}>
                    {[
                      { key: "card", label: t("payment_method_card") },
                      { key: "pix", label: t("payment_method_pix") },
                      { key: "cash", label: t("payment_method_cash") },
                    ].map((pm) => (
                      <div
                        key={pm.key}
                        className={`${classes.pm} ${paymentMethod === pm.key ? classes.pmActive : ""}`}
                        onClick={() => setPaymentMethod(pm.key)}
                      >
                        {pm.label}
                      </div>
                    ))}
                  </div>
                  <button
                    className={`${classes.btn} ${loading ? classes.btnDisabled : ""}`}
                    onClick={confirmSeen}
                    disabled={loading}
                  >
                    {loading ? "..." : t("delivery_confirm_seen_btn")}
                  </button>
                </div>
              )}

              {/* nenhuma forma de pagamento externa disponível */}
              {!showOnline && !showCash && (
                <div className={classes.hint} style={{ textAlign: "center", marginTop: 16 }}>
                  {tt("delivery_no_external_method", "Nenhuma forma de pagamento disponível no momento. Contate o suporte.")}
                </div>
              )}
            </>
          )}

          {step === "received" && (
            <>
              <div className={classes.stepTag}>{t("delivery_step2_title")}</div>
              <Details />
              <button
                className={`${classes.btn} ${loading ? classes.btnDisabled : ""}`}
                onClick={confirmReceived}
                disabled={loading}
              >
                {loading ? "..." : t("delivery_confirm_received_btn")}
              </button>
            </>
          )}

          {step === "done" && (
            <div className={classes.done}>
              <div className={classes.doneIcon}>✅</div>
              <div className={classes.doneTitle}>{t("delivery_done_title")}</div>
              <p>{t("delivery_done_text")}</p>
            </div>
          )}

          {error ? <div className={classes.error}>{error}</div> : null}
        </div>
      </div>
    </div>
  );
}
